import { useEffect, useState } from "react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { track } from "@/lib/analytics";
import { TID } from "@/constants/testIds";
import { Check } from "lucide-react";

/**
 * Standard Contact Form — BRD Section 5A.
 * Fields: Name, Mobile, Email, City, LinkedIn URL or Company+Designation,
 * mandatory Consent checkbox (unchecked by default).
 */
export default function StandardContactForm({ source = "contact_form", ctaLabel = "Send" }) {
  const [values, setValues] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    profile: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    track("form_open", { form: "contact", source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) {
      toast.error("Please tick the consent box before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({ ...values, source });
      setDone(true);
      track("form_submit", { form: "contact", source, email: values.email, mobile: values.mobile, name: values.name });
      toast.success("Thank you. We'll be in touch, unhurriedly.");
    } catch (err) {
      track("form_error", { form: "contact", source });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-forest/20 rounded-lg p-8 bg-white text-center space-y-3">
        <div className="w-12 h-12 rounded-full bg-lime mx-auto grid place-items-center">
          <Check className="text-void" size={22} />
        </div>
        <div className="font-display text-2xl text-forest">Received.</div>
        <div className="text-sm text-forest/70 max-w-md mx-auto leading-relaxed">
          A member of our team will reach out on the number you shared. If you'd
          like to move faster, WhatsApp us at wa.me/918446448123.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <FormField label="Name" required>
          <input
            data-testid={TID.contactName}
            required
            value={values.name}
            onChange={onChange("name")}
            className={inputCls}
          />
        </FormField>
        <FormField label="Mobile" required helper="We'll send updates on WhatsApp only if you consent below.">
          <input
            data-testid={TID.contactMobile}
            required
            inputMode="tel"
            value={values.mobile}
            onChange={onChange("mobile")}
            className={inputCls}
          />
        </FormField>
        <FormField label="Email" required>
          <input
            data-testid={TID.contactEmail}
            required
            type="email"
            value={values.email}
            onChange={onChange("email")}
            className={inputCls}
          />
        </FormField>
        <FormField label="City" required>
          <input
            data-testid={TID.contactCity}
            required
            value={values.city}
            onChange={onChange("city")}
            className={inputCls}
          />
        </FormField>
      </div>
      <FormField
        label="LinkedIn URL or Company & Designation"
        required
        helper="Either input is accepted — helps us understand who we're speaking with."
      >
        <input
          data-testid={TID.contactProfile}
          required
          value={values.profile}
          onChange={onChange("profile")}
          className={inputCls}
          placeholder="linkedin.com/in/… or Acme Corp — VP Engineering"
        />
      </FormField>

      <label className="flex items-start gap-3 pt-2">
        <input
          data-testid={TID.contactConsent}
          type="checkbox"
          required
          checked={values.consent}
          onChange={onChange("consent")}
          className="mt-1 w-4 h-4 accent-[color:var(--phc-forest)]"
        />
        <span className="text-xs text-forest/80 leading-relaxed">
          I consent to being contacted via WhatsApp, Call, and SMS regarding
          Pali Hill City Chapter III. (Required per TRAI regulations.)
        </span>
      </label>

      <button
        data-testid={TID.contactSubmit}
        disabled={submitting}
        className="btn-primary w-full sm:w-auto"
      >
        {submitting ? "Sending…" : ctaLabel}
      </button>
    </form>
  );
}

const inputCls =
  "w-full border-0 border-b border-forest/25 bg-transparent py-3 px-0 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest transition-colors text-[15px] font-body";

function FormField({ label, required, helper, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-forest/60 block mb-1">
        {label} {required && <span className="text-teal">*</span>}
      </span>
      {children}
      {helper && <span className="block mt-1 text-[11px] text-forest/50">{helper}</span>}
    </label>
  );
}
